import { motion } from "framer-motion";
import { Target, Users, TrendingUp, Clock, Heart, Award } from "lucide-react";

const benefits = [
  {
    icon: Target,
    title: "Plan 100% Personalizado",
    description: "Cada rutina se diseña según tus objetivos, tiempo disponible y condición física actual.",
    color: "green",
  },
  {
    icon: Users,
    title: "Acompañamiento Real",
    description: "Un entrenador a tu lado en cada sesión. No te dejamos solo con una app o un PDF.",
    color: "cyan",
  },
  {
    icon: TrendingUp,
    title: "Resultados Medibles",
    description: "Seguimiento de tu progreso con métricas claras. Ves cómo mejoras semana a semana.",
    color: "magenta",
  },
  {
    icon: Clock,
    title: "Horarios Flexibles",
    description: "Adaptamos las sesiones a tu agenda. Sin excusas para no entrenar.",
    color: "green",
  },
  {
    icon: Heart,
    title: "Ambiente Motivador",
    description: "Un espacio donde te sentirás cómodo desde el primer día, sin importar tu nivel.",
    color: "cyan",
  },
  {
    icon: Award,
    title: "Experiencia Comprobada",
    description: "Más de 500 alumnos han transformado su cuerpo y su vida con nosotros.",
    color: "magenta",
  },
];

const colorMap = {
  green: { bg: "rgba(0, 255, 0, 0.1)", text: "#00FF00", shadow: "0 0 30px rgba(0, 255, 0, 0.4)" },
  cyan: { bg: "rgba(0, 255, 255, 0.1)", text: "#00FFFF", shadow: "0 0 30px rgba(0, 255, 255, 0.4)" },
  magenta: { bg: "rgba(255, 0, 255, 0.1)", text: "#FF00FF", shadow: "0 0 30px rgba(255, 0, 255, 0.4)" },
};

export const BenefitsSection = () => {
  return (
    <section 
      id="beneficios"
      data-testid="benefits-section"
      className="relative py-24 bg-dark-paper overflow-hidden"
    >
      {/* Animated background pattern */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        animate={{ 
          backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #00FF00 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-left mb-16"
        >
          <motion.span 
            className="text-neon-green text-sm font-bold uppercase tracking-widest inline-block"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            ¿Por qué Inspirion GYM?
          </motion.span>
          <motion.h2 
            className="mt-4 text-4xl md:text-5xl font-black uppercase tracking-tight text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            No es solo un gimnasio.
            <br />
            <span className="text-neon-cyan text-glow-cyan">Es tu transformación.</span>
          </motion.h2>
          <motion.p 
            className="mt-6 text-lg text-gray-400 max-w-2xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            Cansado de pagar cuotas sin resultados? Aquí cada peso invertido se refleja en tu cuerpo.
          </motion.p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            const colors = colorMap[benefit.color];
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateX: -15 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ 
                  delay: index * 0.1,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="group relative cursor-pointer"
              >
                <motion.div 
                  className="h-full p-8 bg-dark-card border border-white/5 relative overflow-hidden"
                  whileHover={{
                    borderColor: colors.text,
                    boxShadow: colors.shadow,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Animated background on hover */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `radial-gradient(circle at 50% 0%, ${colors.bg}, transparent 70%)`,
                    }}
                  />

                  {/* Icon */}
                  <motion.div 
                    className="relative inline-flex items-center justify-center w-14 h-14 mb-6"
                    style={{ 
                      backgroundColor: colors.bg,
                      boxShadow: `0 0 20px ${colors.bg}`
                    }}
                    whileHover={{ 
                      scale: 1.2,
                      rotate: 5,
                      boxShadow: colors.shadow
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: colors.text }} />
                  </motion.div>
                  
                  {/* Content */}
                  <h3 className="relative text-xl font-bold text-white uppercase tracking-tight mb-3 group-hover:text-white transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="relative text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                    {benefit.description}
                  </p>

                  {/* Animated bottom line */}
                  <motion.div 
                    className="absolute bottom-0 left-0 h-1"
                    style={{ backgroundColor: colors.text }}
                    initial={{ width: 0 }}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.4 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
